import React, { useState, useEffect } from "react";
import { Search, MapPin, Building2, Linkedin, User, Filter, X, Plus, Upload } from "lucide-react";

const RecruiterDashboard = () => {
  const [recruiters, setRecruiters] = useState([]);
  const [allRecruiters, setAllRecruiters] = useState([]);
  const [filters, setFilters] = useState({
    name: "",
    company: "",
    linkedIn: "",
    location: ""
  });
  const [showFilters, setShowFilters] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    linkedIn: "",
    location: ""
  });
  const [bulkData, setBulkData] = useState("");
  const [notification, setNotification] = useState(null);

  const API_URL = import.meta.env.VITE_API_PORT || "http://localhost:5000";

  // Card color schemes - softer, more subtle colors
  const cardColors = [
    { bg: "from-emerald-950/20 to-emerald-900/10", border: "border-emerald-900/20", hover: "hover:border-emerald-700/30", accent: "#34d399", shadow: "shadow-emerald-900/10" },
    { bg: "from-blue-950/20 to-blue-900/10", border: "border-blue-900/20", hover: "hover:border-blue-700/30", accent: "#60a5fa", shadow: "shadow-blue-900/10" },
    { bg: "from-purple-950/20 to-purple-900/10", border: "border-purple-900/20", hover: "hover:border-purple-700/30", accent: "#c084fc", shadow: "shadow-purple-900/10" },
    { bg: "from-rose-950/20 to-rose-900/10", border: "border-rose-900/20", hover: "hover:border-rose-700/30", accent: "#fb7185", shadow: "shadow-rose-900/10" },
    { bg: "from-amber-950/20 to-amber-900/10", border: "border-amber-900/20", hover: "hover:border-amber-700/30", accent: "#fbbf24", shadow: "shadow-amber-900/10" },
    { bg: "from-cyan-950/20 to-cyan-900/10", border: "border-cyan-900/20", hover: "hover:border-cyan-700/30", accent: "#22d3ee", shadow: "shadow-cyan-900/10" },
  ];

  // Show notification
  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Fetch all recruiters
  const loadRecruiters = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/recruiters`);
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      const recruitersArray = Array.isArray(data.data) ? data.data : [];
      setRecruiters(recruitersArray);
      setAllRecruiters(recruitersArray);
    } catch (err) {
      console.error("Failed to fetch recruiters:", err);
      showNotification("Failed to fetch recruiters from server", "error");
      // Mock data for demonstration
      const mockData = [
        {
          _id: "1",
          name: "Aadil Bandukwala",
          company: "HackerRank",
          linkedIn: "https://www.linkedin.com/in/aadilbandukwala",
          location: "India"
        },
        {
          _id: "2",
          name: "Aakash Singh",
          company: "Infosys",
          linkedIn: "https://www.linkedin.com/in/aakashsingh",
          location: "India"
        },
        {
          _id: "3",
          name: "Aaron H.",
          company: "Confidential",
          linkedIn: "https://www.linkedin.com/in/aaronh",
          location: "USA"
        }
      ];
      setRecruiters(mockData);
      setAllRecruiters(mockData);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadRecruiters();
  }, []);

  // Apply filters using backend search or local filtering
  useEffect(() => {
    const applyFilters = async () => {
      const hasFilters = Object.values(filters).some(val => val !== "");
      
      if (!hasFilters) {
        setRecruiters(allRecruiters);
        return;
      }

      // Try backend search first
      try {
        const queryParams = new URLSearchParams();
        if (filters.name) queryParams.append('name', filters.name);
        if (filters.company) queryParams.append('company', filters.company);
        if (filters.linkedIn) queryParams.append('linkedIn', filters.linkedIn);
        if (filters.location) queryParams.append('location', filters.location);

        const res = await fetch(`${API_URL}/api/recruiters/search?${queryParams.toString()}`);
        if (res.ok) {
          const data = await res.json();
          setRecruiters(Array.isArray(data.data) ? data.data : []);
          return;
        }
      } catch (err) {
        console.log("Backend search unavailable, using local filter");
      }

      // Fallback to local filtering
      const filtered = allRecruiters.filter((recruiter) => {
        const nameMatch = !filters.name || recruiter.name?.toLowerCase().includes(filters.name.toLowerCase());
        const companyMatch = !filters.company || recruiter.company?.toLowerCase().includes(filters.company.toLowerCase());
        const linkedInMatch = !filters.linkedIn || recruiter.linkedIn?.toLowerCase().includes(filters.linkedIn.toLowerCase());
        const locationMatch = !filters.location || recruiter.location?.toLowerCase().includes(filters.location.toLowerCase());
        
        return nameMatch && companyMatch && linkedInMatch && locationMatch;
      });
      setRecruiters(filtered);
    };

    const debounceTimer = setTimeout(applyFilters, 300);
    return () => clearTimeout(debounceTimer);
  }, [filters, allRecruiters]);

  // Create recruiter
  const createRecruiter = async () => {
    if (!formData.name || !formData.company || !formData.linkedIn || !formData.location) {
      showNotification("All fields are required", "error");
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/recruiters`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });
      if (!res.ok) throw new Error('Failed to create');
      const data = await res.json();
      setAllRecruiters(prev => [...prev, data]);
      setRecruiters(prev => [...prev, data]);
      setShowAddModal(false);
      setFormData({ name: "", company: "", linkedIn: "", location: "" });
      showNotification("Recruiter added successfully!");
    } catch (err) {
      console.error("Failed to create recruiter:", err);
      showNotification("Failed to add recruiter", "error");
    }
  };

  // Bulk insert
  const bulkInsertRecruiters = async () => {
    try {
      const recruitersArray = JSON.parse(bulkData);
      if (!Array.isArray(recruitersArray)) {
        throw new Error("Data must be an array");
      }
      const res = await fetch(`${API_URL}/api/recruiters/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(recruitersArray)
      });
      if (!res.ok) throw new Error('Failed to bulk insert');
      const data = await res.json();
      loadRecruiters();
      setShowBulkModal(false);
      setBulkData("");
      showNotification(`${data.length} recruiters imported successfully!`);
    } catch (err) {
      console.error("Failed to bulk insert:", err);
      showNotification("Failed to import recruiters. Check JSON format.", "error");
    }
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({ name: "", company: "", linkedIn: "", location: "" });
  };

  const hasActiveFilters = React.useMemo(() => {
    return Object.values(filters).some(val => val !== "");
  }, [filters]);

  // Format LinkedIn URL
  const formatLinkedInUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }
    return `https://${url}`;
  };

  // Get card color based on index
  const getCardColor = (index) => {
    return cardColors[index % cardColors.length];
  };

  return (
    <div className="min-h-screen bg-black p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Notification */}
        {notification && (
          <div className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg border ${
            notification.type === "success" 
              ? "bg-zinc-900 border-[#79e708] text-[#79e708]" 
              : "bg-zinc-900 border-red-500 text-red-500"
          } animate-in slide-in-from-top-2`}>
            {notification.message}
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-2 flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#79e708' }}>
                  <User className="w-7 h-7 text-black" />
                </div>
                Recruiter Hub
              </h1>
              <p className="text-gray-400">Manage your recruiter network efficiently</p>
            </div>
            {/* <div className="flex gap-3">
              <button
                onClick={() => setShowBulkModal(true)}
                className="flex items-center gap-2 px-5 py-3 bg-zinc-900 hover:bg-zinc-800 text-white rounded-xl transition-all font-medium border border-zinc-800"
              >
                <Upload className="w-5 h-5" />
                Bulk Import
              </button>
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2 px-5 py-3 text-black rounded-xl transition-all font-medium hover:opacity-90 shadow-lg"
                style={{ backgroundColor: '#79e708' }}
              >
                <Plus className="w-5 h-5" />
                Add Recruiter
              </button>
            </div> */}
          </div>
        </div>

        {/* Filter Section */}
        <div className="bg-zinc-900/50 backdrop-blur-sm p-6 rounded-2xl shadow-2xl border border-zinc-800/50 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5" style={{ color: '#79e708' }} />
              <h2 className="text-xl font-semibold text-white">Search & Filter</h2>
              {hasActiveFilters && (
                <span className="text-black text-xs px-3 py-1 rounded-full font-semibold" style={{ backgroundColor: '#79e708' }}>
                  Active
                </span>
              )}
            </div>
            <div className="flex gap-2">
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-1"
                >
                  <X className="w-4 h-4" />
                  Clear All
                </button>
              )}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="md:hidden transition-colors"
                style={{ color: '#79e708' }}
              >
                {showFilters ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 ${showFilters ? '' : 'hidden md:grid'}`}>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                placeholder="Search by name..."
                className="w-full pl-10 pr-4 py-3 bg-black/50 border border-zinc-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#79e708] transition-all"
                value={filters.name}
                onChange={(e) => setFilters({ ...filters, name: e.target.value })}
              />
            </div>

            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                placeholder="Search by company..."
                className="w-full pl-10 pr-4 py-3 bg-black/50 border border-zinc-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#79e708] transition-all"
                value={filters.company}
                onChange={(e) => setFilters({ ...filters, company: e.target.value })}
              />
            </div>

            <div className="relative">
              <Linkedin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                placeholder="LinkedIn profile..."
                className="w-full pl-10 pr-4 py-3 bg-black/50 border border-zinc-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#79e708] transition-all"
                value={filters.linkedIn}
                onChange={(e) => setFilters({ ...filters, linkedIn: e.target.value })}
              />
            </div>

            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                placeholder="Search by location..."
                className="w-full pl-10 pr-4 py-3 bg-black/50 border border-zinc-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#79e708] transition-all"
                value={filters.location}
                onChange={(e) => setFilters({ ...filters, location: e.target.value })}
              />
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-6 flex items-center justify-between">
          <div className="text-gray-400">
            Showing <span className="text-white font-semibold text-lg">{recruiters.length}</span> of{" "}
            <span className="text-white font-semibold text-lg">{allRecruiters.length}</span> recruiters
          </div>
        </div>

        {/* Recruiters Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2" style={{ borderColor: '#79e708' }}></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {recruiters.length === 0 ? (
              <div className="col-span-full bg-zinc-900/50 backdrop-blur-sm p-12 rounded-2xl border border-zinc-800/50 text-center">
                <Search className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 text-lg">No recruiters found matching your filters</p>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="mt-4 transition-colors hover:underline"
                    style={{ color: '#79e708' }}
                  >
                    Clear filters to see all recruiters
                  </button>
                )}
              </div>
            ) : (
              recruiters.map((recruiter, index) => {
                const colorScheme = getCardColor(index);
                return (
                  <div
                    key={recruiter._id}
                    className={`bg-gradient-to-br ${colorScheme.bg} backdrop-blur-md p-6 rounded-2xl border ${colorScheme.border} ${colorScheme.hover} transition-all duration-300 group ${colorScheme.shadow} hover:shadow-xl relative overflow-hidden`}
                  >
                    {/* Subtle glow effect */}
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                      <div className="absolute top-0 right-0 w-32 h-32 blur-3xl rounded-full" style={{ backgroundColor: colorScheme.accent + '15' }}></div>
                    </div>
                    
                    <div className="relative z-10">
                      <div className="flex items-start justify-between mb-5">
                        <div className="flex items-center gap-4">
                          <div 
                            className="w-14 h-14 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg backdrop-blur-sm border transition-transform group-hover:scale-110 duration-300"
                            style={{ 
                              backgroundColor: colorScheme.accent + '15', 
                              borderColor: colorScheme.accent + '30',
                              boxShadow: `0 0 20px ${colorScheme.accent}20`
                            }}
                          >
                            {recruiter.name?.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <h3 className="text-xl font-semibold text-white mb-1">
                              {recruiter.name}
                            </h3>
                            <p className="text-gray-400 text-sm flex items-center gap-2">
                              <Building2 className="w-4 h-4 opacity-70" style={{ color: colorScheme.accent }} />
                              <span className="text-gray-300">{recruiter.company}</span>
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3 mb-5">
                        <div className="flex items-center gap-2 text-gray-400">
                          <MapPin className="w-4 h-4 opacity-70" style={{ color: colorScheme.accent }} />
                          <span className="text-sm text-gray-300">{recruiter.location}</span>
                        </div>

                        {recruiter.linkedIn && (
                          <a
                            href={formatLinkedInUrl(recruiter.linkedIn)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 transition-all group/link text-gray-400 hover:text-gray-200"
                          >
                            <Linkedin className="w-4 h-4 opacity-70 group-hover/link:opacity-100 transition-opacity" style={{ color: colorScheme.accent }} />
                            <span className="text-sm group-hover/link:underline text-gray-300">
                              View LinkedIn Profile
                            </span>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Add Recruiter Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-zinc-900 p-8 rounded-2xl border border-zinc-800 max-w-md w-full shadow-2xl">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">Add New Recruiter</h2>
                <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Name *"
                  className="w-full px-4 py-3 bg-black/50 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-[#79e708]"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
                <input
                  type="text"
                  placeholder="Company *"
                  className="w-full px-4 py-3 bg-black/50 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-[#79e708]"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                />
                <input
                  type="text"
                  placeholder="LinkedIn Profile (e.g., linkedin.com/in/username) *"
                  className="w-full px-4 py-3 bg-black/50 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-[#79e708]"
                  value={formData.linkedIn}
                  onChange={(e) => setFormData({ ...formData, linkedIn: e.target.value })}
                />
                <input
                  type="text"
                  placeholder="Location *"
                  className="w-full px-4 py-3 bg-black/50 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-[#79e708]"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                />
                <button
                  onClick={createRecruiter}
                  className="w-full py-3 text-black rounded-xl font-medium transition-all hover:opacity-90"
                  style={{ backgroundColor: '#79e708' }}
                >
                  Add Recruiter
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Import Modal */}
        {showBulkModal && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-zinc-900 p-8 rounded-2xl border border-zinc-800 max-w-2xl w-full shadow-2xl">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">Bulk Import Recruiters</h2>
                <button onClick={() => setShowBulkModal(false)} className="text-gray-400 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <p className="text-gray-400 mb-4">Paste JSON array of recruiter objects:</p>
              <textarea
                placeholder='[{"name": "John Doe", "company": "Tech Corp", "linkedIn": "linkedin.com/in/johndoe", "location": "NYC"}]'
                className="w-full h-64 px-4 py-3 bg-black/50 border border-zinc-700 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-[#79e708]"
                value={bulkData}
                onChange={(e) => setBulkData(e.target.value)}
              />
              <button
                onClick={bulkInsertRecruiters}
                className="w-full mt-4 py-3 text-black rounded-xl font-medium transition-all hover:opacity-90"
                style={{ backgroundColor: '#79e708' }}
              >
                Import Recruiters
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecruiterDashboard;